import { useState, useEffect, useCallback } from 'react';

// Accessibility & display settings. Stored in localStorage (same keys the pre-paint script in
// public/index.html and the native app use) and applied as classes on <html>:
//   dark · hc (alto contraste) · reduce-motion · senior-mode (texto grande)
export const A11Y_KEYS = {
  theme: 'torre_theme',               // 'light' | 'dark' | 'system'
  highContrast: 'torre_high_contrast', // 'true' | 'false'
  reduceMotion: 'torre_reduce_motion', // 'true' | 'false'
  seniorMode: 'torre_senior_mode',     // 'true' | 'false'
};
export const A11Y_CHANGE_EVENT = 'torre-a11y-change';

const read = () => {
  const get = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  const theme = get(A11Y_KEYS.theme);
  return {
    theme: theme === 'light' || theme === 'dark' ? theme : 'system',
    highContrast: get(A11Y_KEYS.highContrast) === 'true',
    reduceMotion: get(A11Y_KEYS.reduceMotion) === 'true',
    seniorMode: get(A11Y_KEYS.seniorMode) === 'true',
  };
};

const systemDark = () => typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-color-scheme: dark)').matches;

function apply(s) {
  const root = document.documentElement;
  const dark = s.theme === 'dark' || (s.theme === 'system' && systemDark());
  root.classList.toggle('dark', dark);
  root.classList.toggle('hc', s.highContrast);
  root.classList.toggle('reduce-motion', s.reduceMotion);
  root.classList.toggle('senior-mode', s.seniorMode);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#0b1220' : '#0F766E');
}

export function useAccessibilitySettings() {
  const [settings, setSettings] = useState(read);

  useEffect(() => { apply(settings); }, [settings]);

  // Inside the native app, tell native so its own chrome (tab bar, Más, loading screens) follows.
  useEffect(() => {
    try {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'A11Y_SETTINGS', ...settings }));
    } catch { /* not in the app */ }
  }, [settings]);

  // Follow the OS theme while on "Automático"; pick up changes made elsewhere
  // (another tab, or the native app injecting new values).
  useEffect(() => {
    const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const onSystem = () => setSettings((s) => { apply(s); return s; });
    const onExternal = () => setSettings(read());
    mq?.addEventListener?.('change', onSystem);
    window.addEventListener('storage', onExternal);
    window.addEventListener(A11Y_CHANGE_EVENT, onExternal);
    return () => {
      mq?.removeEventListener?.('change', onSystem);
      window.removeEventListener('storage', onExternal);
      window.removeEventListener(A11Y_CHANGE_EVENT, onExternal);
    };
  }, []);

  const update = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(A11Y_KEYS.theme, next.theme);
        localStorage.setItem(A11Y_KEYS.highContrast, String(next.highContrast));
        localStorage.setItem(A11Y_KEYS.reduceMotion, String(next.reduceMotion));
        localStorage.setItem(A11Y_KEYS.seniorMode, String(next.seniorMode));
      } catch { /* private mode: settings last for this page view */ }
      return next;
    });
  }, []);

  const toggleSeniorMode = useCallback(() => setSettings((prev) => {
    const next = { ...prev, seniorMode: !prev.seniorMode };
    try { localStorage.setItem(A11Y_KEYS.seniorMode, String(next.seniorMode)); } catch { /* ignore */ }
    return next;
  }), []);

  return { settings, update, toggleSeniorMode };
}
